import { Button, Chip, Grow, Stack, styled } from '@mui/material';
import {
  CONVERSATION_SNOOZE_PRESET_LABELS,
  isConversationSnoozePreset,
} from '@provision/conversation-types';
import { useEffect, useMemo } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { DynamicIcon, PlatformUserAvatar, Tooltip } from 'src/components';

import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  ActionType,
  conversationTypeOptions,
  DataActionRule,
  isDataAction,
  MacroActionRule,
  SystemAction,
  useConversationAssigneeGroupsOptions,
  useConversationParticipantOptions,
  useConversationStatusOptions,
  useConversationUrgencyOptions,
} from 'src/graphql';
import {
  useCurrentUserProfile,
  usePlatformUsersOptions,
} from 'src/graphql/entities/platform-user';
import { useVisibleLabelOptions } from 'src/graphql/entities/visible-label';

const ActionChip = styled(Chip, {
  name: 'ActionChip',
  shouldForwardProp: (prop) => prop !== 'isValid',
})<{ isValid: boolean }>(({ theme, isValid }) => ({
  fontSize: '0.75rem',
  margin: 0,
  gap: 0,
  userSelect: 'none',
  cursor: 'default',
  justifyContent: 'flex-start',
  minWidth: '4rem',
  maxWidth: '10rem',
  ...(isValid
    ? {}
    : {
        background: theme.palette.custom.OV.sys.errorContainer,
      }),
}));

export const ActionChipIcon = styled(DynamicIcon, {
  name: 'ActionChipIcon',
})(() => ({
  width: '1.25rem',
  borderRadius: '50%',
}));

export interface CustomChip {
  id: string;
  icon: React.ReactElement | null;
  label: string;
  tooltip: string;
}

interface AppliedMacroActionsListProps {
  actions: MacroActionRule[];
  partnerId: number;
  customChips?: CustomChip[];
  onActionDelete?: (index: number) => void;
  onReset?: () => void;
}

export const AppliedMacroActionsList = ({
  actions,
  customChips,
  partnerId,
  onActionDelete,
  onReset,
}: AppliedMacroActionsListProps) => {
  const actionChips = useNormalizedActions(actions, partnerId);
  const canReset = Boolean(onReset && actions.length);

  if (!actionChips.length && !customChips?.length && !canReset) return null;

  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center" py={1}>
      <TransitionGroup timeout={{ enter: 500, exit: 250 }} component={null}>
        {customChips?.map((customChip) => {
          return (
            <Grow key={customChip.id} in>
              <Tooltip title={customChip.tooltip}>
                <ActionChip
                  size="small"
                  isValid
                  icon={customChip.icon || undefined}
                  label={customChip.label}
                  onDelete={undefined}
                />
              </Tooltip>
            </Grow>
          );
        })}
        {actionChips.map((action, i) => {
          const { id, label = '', tooltip = label, icon, isValid } = action;
          return (
            <Grow key={id} in>
              <Tooltip title={tooltip}>
                <ActionChip
                  size="small"
                  isValid={isValid}
                  icon={icon || undefined}
                  label={label}
                  onDelete={onActionDelete ? () => onActionDelete(i) : undefined}
                />
              </Tooltip>
            </Grow>
          );
        })}
      </TransitionGroup>
      {canReset ? (
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={onReset}
          sx={{ textTransform: 'none' }}
        >
          Reset macro
        </Button>
      ) : null}
    </Stack>
  );
};

function useNormalizedActions(
  actions: (MacroActionRule & { isValid?: boolean })[],
  partnerId: number,
): {
  label?: string;
  tooltip?: string | React.ReactElement;
  attribute?: string | number;
  icon: React.ReactElement | null;
  isValid: boolean;
  id: string;
}[] {
  const { user } = useCurrentUserProfile();
  const statusOptions = useConversationStatusOptions();
  const urgencyOptions = useConversationUrgencyOptions(partnerId);

  const userActions = actions.filter(
    (action) =>
      isDataAction(action as SystemAction) &&
      (action as SystemAction as DataActionRule).attribute ===
        'Conversation/conversationAssigneeId',
  );

  const { allUserRecords: platformUserOptions } = usePlatformUsersOptions(
    {
      partnerId,
      shouldIncludePlatformPartnerId: user?.partnerId === PLATFORM_PARTNER_ID,
      filters: [
        {
          attribute: 'isActive',
          operator: '=',
          value: 'true',
        },
        {
          attribute: 'userId',
          operator: 'in',
          value: userActions
            .map((action) => (action as SystemAction as DataActionRule).value)
            .join(','),
        },
      ],
    },
    { skip: !userActions.length },
  );
  const assigneeGroupOptions = useConversationAssigneeGroupsOptions(partnerId);

  const { getConversationParticipantOptions, conversationParticipantOptions } =
    useConversationParticipantOptions();

  const labelOptions = useVisibleLabelOptions();

  useEffect(() => {
    const requesterAction = actions.find(
      (action) =>
        'attribute' in action &&
        action.attribute === 'Conversation/requester/participantIdentity',
    );
    if (!requesterAction) return;

    if ('value' in requesterAction) {
      getConversationParticipantOptions({
        partnerId,
        searchText: requesterAction.value as string,
      });
    }
  }, [actions, partnerId, getConversationParticipantOptions]);

  const actionChips = useMemo(() => {
    return actions.map((item) => {
      return {
        ...getChipContent(item as SystemAction, {
          statusOptions,
          urgencyOptions,
          platformUserOptions,
          assigneeGroupOptions,
          conversationParticipantOptions,
          labelOptions,
        }),
        id: item.id!,
        isValid: item.isValid!,
      };
    });
  }, [
    actions,
    statusOptions,
    urgencyOptions,
    platformUserOptions,
    assigneeGroupOptions,
    conversationParticipantOptions,
    labelOptions,
  ]);

  return actionChips;
}

function getChipContent(
  action: SystemAction,
  {
    statusOptions,
    urgencyOptions,
    platformUserOptions,
    assigneeGroupOptions,
    conversationParticipantOptions,
    labelOptions,
  }: {
    statusOptions: ReturnType<typeof useConversationStatusOptions>;
    urgencyOptions: ReturnType<typeof useConversationUrgencyOptions>;
    platformUserOptions: ReturnType<typeof usePlatformUsersOptions>['allUserRecords'];
    assigneeGroupOptions: ReturnType<typeof useConversationAssigneeGroupsOptions>;
    conversationParticipantOptions: ReturnType<
      typeof useConversationParticipantOptions
    >['conversationParticipantOptions'];
    labelOptions: ReturnType<typeof useVisibleLabelOptions>;
  },
) {
  if (isDataAction(action)) {
    if (action.attribute === 'Conversation/subject') {
      return {
        label: action.value,
        attribute: action.attribute,
        icon: <ActionChipIcon name="edit_note" />,
      };
    }
    if (action.attribute === 'Conversation/conversationStatusId') {
      const status = statusOptions.find((item) => item.value === action.value);
      return {
        label: status?.label,
        attribute: action.attribute,
        icon: (
          <ActionChipIcon
            name={status?.icon || 'circle'}
            library={status?.iconLibrary ?? 'lucide'}
          />
        ),
      };
    }
    if (action.attribute === 'Conversation/conversationUrgencyId') {
      const urgency = urgencyOptions.find((item) => item.value === action.value);
      return {
        label: urgency?.label,
        attribute: action.attribute,
        icon: (
          <ActionChipIcon
            name={urgency?.icon || 'priority_high'}
            library={urgency?.iconLibrary}
          />
        ),
      };
    }
    if (action.attribute === 'Conversation/type') {
      const type = conversationTypeOptions.find(
        (item) => (item.id as string) === action.value,
      );

      return {
        label: type?.label,
        attribute: action.attribute,
        icon: <ActionChipIcon name="support_agent" />,
      };
    }
    if (action.attribute === 'Conversation/conversationAssigneeId') {
      const user = platformUserOptions.find((item) => item.value === action.value);
      return {
        label: user?.label,
        attribute: action.attribute,
        icon: (
          <PlatformUserAvatar
            sx={{
              width: '1rem',
              height: '1rem',
              fontSize: '0.65rem !important',
            }}
            name={user?.label || ''}
            partnerId={user?.partnerId}
            avatarFileKey={user?.avatarFileKey}
          />
        ),
      };
    }
    if (action.attribute === 'Conversation/conversationAssigneeGroupId') {
      const assigneeGroup = assigneeGroupOptions.find((item) => item.value === action.value);
      return {
        label: assigneeGroup?.label,
        attribute: action.attribute,
        icon: <ActionChipIcon name="group" />,
      };
    }
    if (action.attribute === 'Conversation/requester/participantIdentity') {
      const user = conversationParticipantOptions.find((item) =>
        item.value.includes(action.value),
      );
      return {
        label: user?.label,
        attribute: action.attribute,
        icon: <ActionChipIcon name="person" />,
      };
    }
    if (action.attribute === 'Conversation/snoozeUntil') {
      return {
        label: `${action.value} hour(s)`,
        attribute: action.attribute,
        icon: <ActionChipIcon name="snooze" />,
      };
    }
    if (action.attribute === 'Conversation/snoozePreset') {
      return {
        label: isConversationSnoozePreset(action.value)
          ? CONVERSATION_SNOOZE_PRESET_LABELS[action.value]
          : action.value,
        attribute: action.attribute,
        icon: <ActionChipIcon name="snooze" />,
      };
    }

    return {
      label: action.value,
      attribute: action.attribute,
      icon: null,
    };
  }
  if ('type' in action) {
    if (action.type === ActionType.Signal) {
      const operationLabel =
        action.operation === 'resolve' ? 'Resolve Signal' : 'Trigger Signal';
      const targetLabel =
        action.notificationsTargetType === 'platform' ? 'Internal' : 'Partner';
      const tooltip =
        action.operation === 'create' ? `Target: ${targetLabel}` : 'Resolve Signal incident';

      return {
        label: operationLabel,
        tooltip,
        attribute: `${action.type}/${action.operation}`,
        icon: <ActionChipIcon name="notification_important" />,
      };
    }
    if (action.type === ActionType.ManageConversationLabels) {
      const labelNames =
        action.labelIds?.map((id) => labelOptions.find((item) => item.value === id)?.label) ||
        [];
      const label =
        labelNames.length > 1 ? `${labelNames[0]} +${labelNames.length - 1}` : labelNames[0];
      const tooltip = <Stack sx={{ whiteSpace: 'pre-wrap' }}>{labelNames.join('\n')}</Stack>;

      return {
        label,
        tooltip,
        attribute: `${action.type}/${action.operation}`,
        icon: <ActionChipIcon name={action.operation === 'add' ? 'label' : 'label_off'} />,
      };
    }
  }
  return {
    label: '',
    attribute: '',
    icon: null,
  };
}
